import useTheme from "../../../../common/hooks/useTheme";
import React, { useEffect, useState } from "react";
import { DraggedCard } from "./Card";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import SelectedContainer from "./SelectedContainer";
import UnselectedContainer from "./UnselectedContainer";
import InfoIcon from "../../svg/InfoIcon";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import { Modal } from "@mui/material";
import {
  getPlatformData,
  UpdateCostumerSelectedBiomarkers,
  UpdateCostumerSelectedMetrics,
} from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import PageLoader from "../../../../common/ui/PageLoader";
import { platformActions } from "../../store/platform-slice";
import { useDispatch } from "react-redux";
import { isMobile } from "react-device-detect";
import CustomDragLayer from "./CustomDragLayer";

type Props = {
  data: any;
  type: string;
  openModal: boolean;
  handleClose: () => void;
  title: string;
  setOpenSnackBar: (value: boolean) => void;
};

const DraggableCardsModal = ({
  data,
  type,
  openModal,
  title,
  handleClose,
  setOpenSnackBar,
}: Props) => {
  const theme = useTheme();
  const {
    ASSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
  } = useAppStore();
  const { AuthIsSimpleUser, AuthUser } = useAuth();
  const dispatch = useDispatch();
  const initialData = data.slice();
  const [isLoading, setIsLoading] = useState(false);
  const initialSelectedCards = initialData.filter((el: any) => el.selected);
  const [selectedCards, setSelectedCards] = useState(() => {
    const filtered = initialData.filter((el: any) => el.selected);
    return [...filtered, ...Array(Math.max(0, 4 - filtered.length)).fill(null)];
  });

  const [unselectedCards, setUnselectedCards] = useState(
    initialData.filter((el: any) => !el.selected)
  );

  useEffect(() => {
    if (openModal) {
      setSelectedCards(() => {
        const filtered = initialData.filter((el: any) => el.selected);
        return [
          ...filtered,
          ...Array(Math.max(0, 4 - filtered.length)).fill(null),
        ];
      });
      setUnselectedCards(initialData.filter((el: any) => !el.selected));
    }
  }, [openModal]);

  const areArraysEqualById = (arr1: any[], arr2: any[]) => {
    const filteredArr2 = arr2.filter((item) => item !== null);
    if (arr1.length !== filteredArr2.length) return false;
    return arr1.every((item, index) => {
      if (!item || !filteredArr2[index]) return item === filteredArr2[index]; // Handle `null` values
      return item.id === filteredArr2[index].id;
    });
  };

  const handleSelectedDrop = (
    draggedCard: DraggedCard,
    targetIndex: number
  ) => {
    const dragged = draggedCard.fromSelected
      ? selectedCards[draggedCard.index]
      : unselectedCards.find((card: any) => card.id === draggedCard.id);

    if (!dragged) return;

    const updatedSelectedCards = [...selectedCards];
    const targetCard = updatedSelectedCards[targetIndex];

    if (draggedCard.fromSelected) {
      // Swap positions if dragging within the selected container
      updatedSelectedCards[draggedCard.index] = targetCard;
    } else {
      // Add the target card back to unselected if it exists
      if (targetCard) {
        setUnselectedCards((prev: any) => [targetCard, ...prev]);
      }

      // Remove the dragged card from unselected
      setUnselectedCards((prev: any) =>
        prev.filter((card: any) => card.id !== draggedCard.id)
      );
    }

    // Place the dragged card in the target position
    updatedSelectedCards[targetIndex] = dragged;
    setSelectedCards(updatedSelectedCards);
  };

  const handleUnselectedDrop = (draggedCard: DraggedCard) => {
    if (draggedCard.fromSelected) {
      const dragged = selectedCards[draggedCard.index];
      const updatedSelectedCards = [...selectedCards];

      // @ts-ignore
      updatedSelectedCards[draggedCard.index] = null;

      if (dragged) {
        setUnselectedCards((prev: any) => [dragged, ...prev]);
      }

      setSelectedCards(updatedSelectedCards);
    }
  };

  const handleCloseFunction = () => {
    if (!areArraysEqualById(initialSelectedCards, selectedCards)) {
      setOpenSnackBar(true);
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const idsArray: string[] = selectedCards
        .filter((item: any) => item && item.id)
        .map((item: any) => item.id);

      const id = AuthIsSimpleUser
        ? AuthUser.pnoe_user_id
        : ASSelectedClient?.id;

      if (!id) return;

      if (idsArray.length === 0) {
        ASSetOpenSnackBar(true);
        ASSetSnackBarMessage({
          title: `Error`,
          message: `Please select at least one metric`,
        });
        ASSetTypeSnackBar("alert");
        setIsLoading(false);
        return;
      }

      if (type === "metric") {
        await UpdateCostumerSelectedMetrics(id, idsArray);
      }

      if (type === "biomarker") {
        await UpdateCostumerSelectedBiomarkers(id, idsArray);
      }

      const dataPlatform = await getPlatformData(id, AuthUser.role);

      dispatch(platformActions.updateDataPlatformServer(dataPlatform));

      setIsLoading(false);
      handleClose();
    } catch (error: any) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Error ${error.status}`,
        message: `Failed to update selected metrics`,
      });
      ASSetTypeSnackBar("alert");
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={handleCloseFunction}
      disableEnforceFocus
      // sx={{ zIndex: 2000 }}
    >
      <DndProvider backend={HTML5Backend}>
        <CustomDragLayer />
        <div style={styles.modalContainer(theme)}>
          <div style={styles.modalHeaderContainer(isMobile)}>
            <div style={styles.modalHeaderTitle(theme)}>{title}</div>
            <div onClick={handleCloseFunction}>
              <XIconSvg />
            </div>
          </div>
          {isLoading ? (
            <PageLoader />
          ) : (
            <>
              <div style={styles.modalSectionTitle(theme)}>On overview</div>
              <SelectedContainer
                cards={selectedCards}
                onCardDrop={handleSelectedDrop}
                type={type}
              />
              <div
                style={{
                  ...styles.modalSectionTitle(theme),
                  marginTop: "24px",
                }}
              >
                Not on overview
              </div>
              <UnselectedContainer
                cards={unselectedCards}
                onCardDrop={handleUnselectedDrop}
                type={type}
              />
              <div style={styles.footerContainer(isMobile)}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div style={{ width: "16px", height: "16px" }}>
                    <InfoIcon color={theme.colors_ds.NEUTRAL_ICON_WEAK} />
                  </div>
                  <div style={styles.footerMessage(theme, isMobile)}>
                    Drag and drop cards to rearrange them, and add or remove
                    cards in the overview. A maximum of four cards can be
                    displayed.
                  </div>
                </div>
                <div>
                  <PrimaryButton
                    label={"Save"}
                    isDisable={areArraysEqualById(
                      initialSelectedCards,
                      selectedCards
                    )}
                    onClick={handleSave}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </DndProvider>
    </Modal>
  );
};

export default DraggableCardsModal;

const styles = {
  modalContainer: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "350px",
    height: "90%", // Allow modal to use most of the screen height
    borderRadius: "8px",
  }),
  footerContainer: (isMobile: boolean) => ({
    marginTop: "16px",
    display: "flex",
    // flexDirection: isMobile ? "column" : "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  }),
  footerMessage: (theme: any, isMobile: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: isMobile ? "10px" : "12px",
    lineHeight: isMobile ? "12px" : "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  modalHeaderContainer: (isMobile: boolean) => ({
    display: "flex",
    gap: "16px",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: isMobile ? "8px" : "40px",
  }),
  modalHeaderTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    flex: 1,
  }),
  modalSectionTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    marginBottom: "16px",
  }),
};
